import React, { useState, useEffect } from 'react';
import {
    Backdrop,
    Card,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    ListItemText,
    OutlinedInput,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CustomFilter from 'src/components/filter/CustomFilter';
import Iconify from 'src/components/iconify';
import ClearButton from 'src/components/shared/ClearButton';
import DynamicTable from 'src/components/shared/DynamicTable';
import FilterButton from 'src/components/shared/FilterButton';
import FilterViewButton from 'src/components/shared/FilterViewButton';
import Services from 'src/services/Services';
import moment from 'moment';
import { toast } from 'react-toastify';

export default function EticaretReportList({ parsedUser, tabs }) {
    const [reportList, setReportList] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterModal, setFilterModal] = useState(false);
    const [filters, setFilters] = useState({});
    const isMobile = useMediaQuery('(max-width:768px)');
    const [openDetail, setOpenDetail] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [formData, setFormData] = useState({});

    const uniqueValues = (key) =>
        Array.from(new Set((originalData || []).map(x => x?.[key]).filter(Boolean)))
            .sort((a, b) => String(a).localeCompare(String(b), "tr-TR"));

    const formatDate = (timestampOrMoment) => {
        if (moment.isMoment(timestampOrMoment)) return timestampOrMoment.format("DD.MM.YYYY");
        if (timestampOrMoment == null && timestampOrMoment !== 0) return "-";
        const date = new Date(timestampOrMoment * 1000);
        const year = date.getFullYear();
        if (isNaN(year) || year === 1970) return "-";
        const day = String(date.getDate()).padStart(2,'0');
        const month = String(date.getMonth()+1).padStart(2,'0');
        return `${day}.${month}.${year}`;
    };

    const TABLE_HEAD = [
        { id: 'eCommerceBrand', label: "E-Ticaret Markası" },
        { id: 'inBranchDate', label: "Kargo Geliş Tarihi", format: (value) => value ? formatDate(value) : "-" },
        { id: 'barcode', label: "Barkod Numarası", format: (value) => value ? value : "-" },
        { id: 'preprintedBarcode', label: "Pre-Printed Barkod", format: (value) => value ? value : "-" },
        { id: 'returnBarcode', label: "İade Kodu", format: (value) => value ? value : "-" },
        { id: 'receiverName', label: "Alıcı Müşteri Adı", format: (value) => value ? value : "-" },
        { id: 'takenByName', label: "Teslim Alan Müşteri Adı", format: (value) => value ? value : "-" },
        { id: 'deliveredStaff', label: "Teslim Eden EP Çalışanı", format: (value) => value ? value : "-" },
        { id: 'poiTitle', label: "Lokasyon", format: (value) => value ? value : "-" },
        { id: 'deliveredDate', label: "Müşteriye Teslim Tarihi", format: (value) => value ? formatDate(value) : "-" },
        { id: 'processingType', label: "İşlem Türü", format: (value) => value ? value : "-" },
        { id: 'returnDate', label: "İade Tarihi", format: (value) => value ? formatDate(value) : "-" },
        {
            id: 'action',
            label: '',
            alignRight: false,
            format: (value, row) => (
                <Tooltip title={"Detay"}>
                    <Iconify
                        style={{ color: '#98A2B3', width: 22, height: 22, cursor: 'pointer' }}
                        icon="bx:detail"
                        onClick={() => handleOpenDetail(row)}
                    />
                </Tooltip>
            ),
        }
    ];

    // --- TEK NOKTA: filtre uygulayıcı ---
    const applyFilters = (data, currentFilters) => {
        if (!currentFilters || Object.keys(currentFilters).length === 0) return data;

        const asJsDate = (val, type="start") => {
            if (!val) return null;
            if (moment.isMoment(val)) return (type === "start" ? val.startOf("day") : val.endOf("day")).toDate();
            if (val instanceof Date) return val;
            return new Date(val);
        };

        return (data || []).filter(item =>
            Object.entries(currentFilters).every(([key, filterValue]) => {
                // Tarihler
                if (key === 'returnDate' || key === 'inBranchDate') {
                    const itemDate = item[key] ? new Date(item[key] * 1000) : null;
                    const start = asJsDate(filterValue?.start, "start");
                    const end = asJsDate(filterValue?.end, "end");
                    if ((start || end) && !itemDate) return false;
                    if (start && itemDate && itemDate < start) return false;
                    if (end && itemDate && itemDate > end) return false;
                    return true;
                }
                // Çoklu seçim
                if (Array.isArray(filterValue)) {
                    if (!filterValue.length) return true;
                    const raw = item[key];
                    const val = typeof raw === "string" ? raw : String(raw ?? "");
                    return filterValue.map(String).includes(val);
                }
                // Metin arama
                if (!filterValue || (typeof filterValue === "string" && filterValue.trim() === "")) return true;

                let itemValue = item[key];
                if (typeof itemValue === "number") itemValue = String(itemValue);

                const columnDef = TABLE_HEAD.find(col => col.id === key);
                if (columnDef?.format) {
                    try { itemValue = columnDef.format(itemValue, item); } catch {}
                }

                return (
                    itemValue &&
                    String(itemValue).toLocaleUpperCase("tr-TR")
                        .includes(String(filterValue).toLocaleUpperCase("tr-TR"))
                );
            })
        );
    };

    useEffect(() => {
        getReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // preserveFilters parametresi eklendi
    const getReports = (preserveFilters = false) => {
        setLoading(true);
        Services.GetOperationReport(parsedUser.token, "e-commerce")
            .then((res) => {
                if (res.data.status) {
                    const incoming = res.data.result || [];
                    setOriginalData(incoming);

                    const nextList =
                        preserveFilters && Object.keys(filters || {}).length > 0
                            ? applyFilters(incoming, filters)
                            : incoming;

                    setReportList(nextList);
                } else {
                    toast.error("Rapor bulunamadı.");
                    setReportList([]);
                    setOriginalData([]);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const handleModal = () => setFilterModal(true);
    const handleCloseModal = () => setFilterModal(false);

    const getFilteredECommerceReports = () => {
        const filteredData = applyFilters(originalData, filters);
        setReportList(filteredData);
        handleCloseModal();
    };

    const clearFilters = () => {
        setFilters({});
        setReportList(originalData);
    };

    const handleOpenDetail = (row) => {
        const inBranchDate = row.inBranchDate ? moment(row.inBranchDate * 1000) : null;
        const deliveredDate = row.deliveredDate ? moment(row.deliveredDate * 1000) : null;
        const returnDate = row.returnDate > 0 ? moment(row.returnDate * 1000) : null;

        setSelectedRow(row);
        setFormData({
            ...row,
            inBranchDate,
            deliveredDate,
            returnDate
        });
        setOpenDetail(true);
    };

    const handleCloseDetail = () => {
        setOpenDetail(false);
        setSelectedRow(null);
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const isDetailFormValid = () => {
        if (!formData) return false;

        let requiredFields = [];
        if (formData.processingType === "Noktada") {
            requiredFields = [
                'eCommerceBrand','inBranchDate','barcode','receiverName','takenByName',
                'deliveredStaff','processingType','returnDate','deliveredDate',
                'preprintedBarcode','returnBarcode'
            ];
        } else if (formData.processingType === "İade Edilecek") {
            requiredFields = ['eCommerceBrand','inBranchDate','barcode','receiverName'];
        } else if (formData.processingType === "İade Edildi") {
            requiredFields = [
                'eCommerceBrand','inBranchDate','barcode','preprintedBarcode',
                'returnBarcode','deliveredStaff','returnDate'
            ];
        } else if (formData.processingType === "Teslim") {
            requiredFields = [
                'eCommerceBrand','inBranchDate','barcode','receiverName',
                'takenByName','deliveredStaff','processingType','deliveredDate'
            ];
        }
        for (let field of requiredFields) {
            if (!formData[field]) return false;
            if (typeof formData[field] === 'string' && formData[field].trim() === '') return false;
        }
        return true;
    };

    const handleSaveDetail = () => {
        const formattedData = {
            operationID: formData.operationID,
            eCommerceBrand: formData.eCommerceBrand,
            inBranchDate: formData.inBranchDate ? formData.inBranchDate.format("YYYY-MM-DD") : null,
            barcode: formData.barcode,
            returnBarcode: formData.returnBarcode,
            receiverName: formData.receiverName,
            takenByName: formData.takenByName,
            deliveredStaff: formData.deliveredStaff,
            processingType: formData.processingType,
            returnDate: formData.returnDate ? formData.returnDate.format("YYYY-MM-DD") : "1970-01-01",
            deliveredDate: formData?.deliveredDate ? formData.deliveredDate.format("YYYY-MM-DD") : null,
            preprintedBarcode: formData.preprintedBarcode
        };

        setLoading(true);
        Services.UpdateOperationReport(parsedUser.token, [formattedData], "e-commerce")
            .then((res) => {
                if (res.data.status) {
                    // Filtreleri KORUYARAK yenile
                    getReports(true);
                    handleCloseDetail();
                    toast.success("Rapor başarıyla güncellendi.");
                } else {
                    console.log(res.data);
                    toast.error("Rapor güncellenirken hata oluştu.");
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    return (
        <div>
            <Stack direction="row" justifyContent="space-between" mb={2}>
                {tabs}
            </Stack>
            <Stack direction="row" justifyContent={isMobile ? "space-between" : "end"} mb={5} paddingX={2}>
                {isMobile ? <FilterViewButton variant="contained" onClick={handleModal} /> : null}
            </Stack>

            <CustomFilter modalType="drawer" filterModal={filterModal} handleCloseModal={handleCloseModal}>
                <Stack direction="column" alignItems="flex-start" justifyContent="flex-start" paddingY={2} paddingX={1}>
                    {TABLE_HEAD.map((column) => {
                        if (column.id === "inBranchDate" || column.id === "returnDate") {
                            return (
                                <Stack key={column.id} direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label={`${column.label} Başlangıç`}
                                            format="DD/MM/YYYY"
                                            value={filters[column.id]?.start || null}
                                            onChange={(newValue) => {
                                                setFilters({
                                                    ...filters,
                                                    [column.id]: { ...(filters[column.id] || {}), start: newValue }
                                                });
                                            }}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                        <DatePicker
                                            label={`${column.label} Bitiş`}
                                            format="DD/MM/YYYY"
                                            value={filters[column.id]?.end || null}
                                            onChange={(newValue) => {
                                                setFilters({
                                                    ...filters,
                                                    [column.id]: { ...(filters[column.id] || {}), end: newValue }
                                                });
                                            }}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </LocalizationProvider>
                                </Stack>
                            );
                        }
                        else if (["eCommerceBrand", "poiTitle", "processingType"].includes(column.id)) {
                            const options = uniqueValues(column.id);
                            return (
                                <FormControl key={column.id} fullWidth margin="normal" variant="outlined">
                                    <InputLabel id={`${column.id}-label`}>{column.label}</InputLabel>
                                    <Select
                                        labelId={`${column.id}-label`}
                                        multiple
                                        input={<OutlinedInput label={column.label} />}
                                        renderValue={(selected) => (selected || []).join(", ")}
                                        value={filters[column.id] || []}
                                        onChange={(e) => {
                                            const value = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                                            setFilters({ ...filters, [column.id]: value });
                                        }}
                                    >
                                        {options.map(opt => (
                                            <MenuItem key={opt} value={opt}>
                                                <Checkbox checked={(filters[column.id] || []).indexOf(opt) > -1} />
                                                <ListItemText primary={opt} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            );
                        }
                        else if (column.id === "description") {
                            return (
                                <FormControl key={column.id} fullWidth margin="normal" variant="outlined">
                                    <InputLabel id="description-label">{column.label}</InputLabel>
                                    <Select
                                        labelId="description-label"
                                        id="description"
                                        value={filters[column.id] || ''}
                                        label={column.label}
                                        onChange={(e) =>
                                            setFilters({ ...filters, [column.id]: e.target.value })
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Seçiniz</em>
                                        </MenuItem>
                                        {['Hazır Gönderi', 'Ücretli Gönderi', 'Amazon İadesi', 'DHL Dağıtım'].map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            );
                        } else if (column.label) {
                            return (
                                <TextField
                                    key={column.id}
                                    label={column.label}
                                    value={filters[column.id] || ''}
                                    onChange={(e) =>
                                        setFilters({ ...filters, [column.id]: e.target.value })
                                    }
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                />
                            );
                        }
                        return null;
                    })}
                </Stack>
                <Divider sx={{ width: "100%", paddingTop: "10px" }} />
                <Stack mr={2} direction="row" alignItems="center" justifyContent="center" paddingY={2} paddingX={1}>
                    <ClearButton onClick={() => clearFilters()} />
                    <FilterButton onClick={() => getFilteredECommerceReports()} />
                </Stack>
            </CustomFilter>

            <Card>
                {loading ? (
                    <Backdrop sx={{ color: "#fff", zIndex: 2, height: "100%" }} open={loading}>
                        <CircularProgress color="inherit" />
                    </Backdrop>
                ) : (
                    <>
                        <DynamicTable
                            data={reportList}
                            headers={TABLE_HEAD}
                            isOrdered={true}
                            initialOrder="inBranchDate"
                            initalOrderDirection="desc"
                            isMobile={isMobile}
                            handleModal={handleModal}
                            handleCloseModal={handleCloseModal}
                        />
                        <Dialog open={openDetail} onClose={handleCloseDetail} fullWidth maxWidth="sm">
                            <DialogTitle>Detay Düzenle</DialogTitle>
                            <DialogContent>
                                <Stack spacing={2} mt={1}>
                                    <FormControl fullWidth>
                                        <InputLabel id="eCommerce-detail-label">E-Ticaret Markası</InputLabel>
                                        <Select
                                            labelId="eCommerce-detail-label"
                                            value={formData.eCommerceBrand || ''}
                                            label="E-Ticaret Markası"
                                            onChange={(e) => handleFormChange('eCommerceBrand', e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>Seçiniz</em>
                                            </MenuItem>
                                            {['HEPSİBURADA','TRENDYOL','N11','ARAS KARGO','EPX','MANUEL'].map((option) => (
                                                <MenuItem key={option} value={option}>{option}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label="Kargo Geliş Tarihi"
                                            format="DD/MM/YYYY"
                                            value={formData.inBranchDate || null}
                                            onChange={(newValue) => handleFormChange('inBranchDate', newValue)}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </LocalizationProvider>

                                    <TextField
                                        label="Barkod Numarası"
                                        type="text"
                                        value={formData.barcode || ''}
                                        onChange={(e) => handleFormChange('barcode', e.target.value)}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Pre-Printed Barkod"
                                        type="number"
                                        value={formData.preprintedBarcode || ''}
                                        onChange={(e) => handleFormChange('preprintedBarcode', e.target.value)}
                                        fullWidth
                                    />
                                    <TextField
                                        label="İade Kodu"
                                        value={formData.returnBarcode || ''}
                                        onChange={(e) => handleFormChange('returnBarcode', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Alıcı Müşteri Adı"
                                        value={formData.receiverName || ''}
                                        onChange={(e) => handleFormChange('receiverName', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Teslim Alan Müşteri Adı"
                                        value={formData.takenByName || ''}
                                        onChange={(e) => handleFormChange('takenByName', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Teslim Eden EP Çalışanı"
                                        value={formData.deliveredStaff || ''}
                                        onChange={(e) => handleFormChange('deliveredStaff', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label="Müşteriye Teslim Tarihi"
                                            format="DD/MM/YYYY"
                                            value={formData.deliveredDate || null}
                                            onChange={(newValue) => handleFormChange('deliveredDate', newValue)}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </LocalizationProvider>
                                    <FormControl fullWidth>
                                        <InputLabel id="processingType-detail-label">İşlem Türü</InputLabel>
                                        <Select
                                            labelId="processingType-detail-label"
                                            value={formData.processingType || ''}
                                            label="İşlem Türü"
                                            onChange={(e) => handleFormChange('processingType', e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>Seçiniz</em>
                                            </MenuItem>
                                            {['Teslim', 'İade Edilecek', 'İade Edildi', 'Noktada'].map((option) => (
                                                <MenuItem key={option} value={option}>{option}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label="İade Tarihi"
                                            format="DD/MM/YYYY"
                                            value={formData.returnDate || null}
                                            onChange={(newValue) => handleFormChange('returnDate', newValue)}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </LocalizationProvider>
                                </Stack>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseDetail}>İptal</Button>
                                <Button onClick={handleSaveDetail} variant="contained" disabled={!isDetailFormValid()}>
                                    Kaydet
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </>
                )}
            </Card>
        </div>
    );
}